import React from 'react';
import styled from 'styled-components';

import { font } from '../../../style/text';
import ReactMarkdown from 'react-markdown';
import { isMobileDevice } from '../../../style/utils';
import { getLocalizedTexts } from '../../../Locales';
import { EnrichedProductDiscoveryConversationMessage } from '../../../lib/productDiscovery/conversationMessages';

const MINIMUM_DISPLAY_TIME_FOR_PLACEHOLDER_MESSAGE_IN_MS = 500;
const PLACEHOLDER_PHASE_DURATION_IN_MS = 5000;

export function AssistantMessageContent({
    message,
    sentMessageTimestampRef,
}: {
    message: EnrichedProductDiscoveryConversationMessage;
    sentMessageTimestampRef?: React.MutableRefObject<{ messageIndex: number; timestamp: number } | undefined>;
}) {
    const { placeholderMessage, shouldShowPlaceholderMessage } = usePlaceholderMessage(
        message.content,
        sentMessageTimestampRef
    );
    return shouldShowPlaceholderMessage ? (
        <GradientThinkingMessage>{placeholderMessage}</GradientThinkingMessage>
    ) : (
        <MessageAssistantText>
            <FormattedMessage>{message.content}</FormattedMessage>
        </MessageAssistantText>
    );
}

/**
 * This hook shows a sequence of assistant loading messages to the user while fetching search results.
 *
 * To ensure readability, the last shown message is displayed for a minimum of 500ms, even if
 * results arrive during that window. This only happens for a total latency between 5-5.5s, 10-10.5s and 15-15.5s.
 */
export function usePlaceholderMessage(
    messageContent: string | undefined,
    sentMessageTimestampRef?: React.MutableRefObject<{ messageIndex: number; timestamp: number } | undefined>
) {
    const [placeholderMessage, setPlaceholderMessage] = React.useState<string>('');
    const [placeholderPhaseStartTime, setPlaceholderPhaseStartTime] = React.useState<number>(Date.now());
    const [shouldShowPlaceholderMessage, setShouldShowPlaceholderMessage] = React.useState<boolean>(false);
    const texts = getLocalizedTexts().productDiscovery.placeholderMessages;
    React.useEffect(() => {
        const timers: NodeJS.Timeout[] = [];
        if (!sentMessageTimestampRef || !sentMessageTimestampRef.current) return;
        const timeSinceMessageSent = Date.now() - sentMessageTimestampRef.current.timestamp;
        const placeholderPhases = [
            { delay: 0, message: texts.firstPlaceholderPhase },
            {
                delay: PLACEHOLDER_PHASE_DURATION_IN_MS,
                message: texts.secondPlaceholderPhase,
            },
            {
                delay: 2 * PLACEHOLDER_PHASE_DURATION_IN_MS,
                message: texts.thirdPlaceholderPhase,
            },
            {
                delay: 3 * PLACEHOLDER_PHASE_DURATION_IN_MS,
                message: texts.fourthPlaceholderPhase,
            },
        ];
        for (const { delay, message } of placeholderPhases) {
            const timeout = setTimeout(() => {
                setPlaceholderMessage(message);
                setPlaceholderPhaseStartTime(Date.now());
            }, Math.max(delay - timeSinceMessageSent, 0));
            timers.push(timeout);
        }
        return () => {
            for (const timeout of timers) clearTimeout(timeout);
        };
    }, [sentMessageTimestampRef?.current?.timestamp]);
    React.useEffect(() => {
        if (!messageContent) {
            setShouldShowPlaceholderMessage(true);
            return;
        }
        const timeSincePlaceholderPhaseStart = Date.now() - placeholderPhaseStartTime;
        if (timeSincePlaceholderPhaseStart >= MINIMUM_DISPLAY_TIME_FOR_PLACEHOLDER_MESSAGE_IN_MS)
            setShouldShowPlaceholderMessage(false);
        else {
            const timeoutId = setTimeout(() => {
                setShouldShowPlaceholderMessage(false);
            }, MINIMUM_DISPLAY_TIME_FOR_PLACEHOLDER_MESSAGE_IN_MS - timeSincePlaceholderPhaseStart);
            return () => clearTimeout(timeoutId);
        }
    }, [messageContent, placeholderPhaseStartTime]);
    return { placeholderMessage, shouldShowPlaceholderMessage };
}

const MessageAssistantText = styled.div`
    margin-top: 4px;
    font-size: 16px;
    font-family: ${font.ambitSemiBold};
    line-height: ${isMobileDevice ? '22px' : '120%'};
`;

// ReactMarkdown wraps text content in <p> tags, which typically have a default margin applied by browsers.
// This is a workaround to remove the margin.
const FormattedMessage = styled(ReactMarkdown)`
    p {
        margin: 0;
    }
`;

const GradientThinkingMessage = styled.div`
    max-width: 100%;
    margin-top: 4px;
    ${isMobileDevice ? 'margin-bottom: 16px;' : ''}
    background: linear-gradient(90deg, #a1a1a1, #e2e2e2, #a1a1a1);
    background-size: 200% 100%;
    font-size: 16px;
    font-family: ${font.ambitSemiBold};
    line-height: 22px;
    ${isMobileDevice ? '' : 'white-space: nowrap;'}
    animation: loading-gradient 3s infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    @keyframes loading-gradient {
        0% {
            background-position: 100% 0;
        }

        100% {
            background-position: 0 0;
        }
    }
`;
